<template>
  <div>
    <h1>Roles</h1>

    <div
      v-for="(example, index) in examples"
      :key="index"
      class="p-lg mb-xxl bg-whiteOverride rounded-sm"
    >
      <h2>
        {{ example.templates.length }} Templates
        {{ example.parties.length }} Parties
      </h2>

      <RolesSelector
        :parties="example.parties"
        :templates="example.templates"
        @select="example.selectRole"
        @add="example.addParty"
        @remove="example.removeParty"
        @toggle="example.togglePartyRole"
      />

      <hr />

      <div class="flex justify-between items-start gap-lg">
        <pre
          v-if="showParties"
          v-for="(party, index) in example.parties"
          :key="index"
          >{{ JSON.stringify(party, null, 2) }}
        </pre>
        <BaseButton
          class="ml-auto"
          icon="arrow-down"
          @click.native="showParties = !showParties"
        >
          json
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script>
// @ts-check
import { defineComponent, ref } from 'vue';
import { faker } from '@faker-js/faker';

import { useBulkSendRoles } from '@/composables/bulkSend/roles';
import { createNewParties } from '@/store/modules/bulkSend/factories/parties';
import {
  createTemplateParties,
  createTemplates,
} from '@/store/modules/bulkSend/factories/templates';

import BaseButton from '@/components/library/buttons/BaseButton.vue';
import RolesSelector from '@/components/ui/bulkSend/RolesSelector.vue';

/** @typedef {import('@/store/modules/bulkSend/models/Parties').TUpdateParties} TUpdateParties */
/** @typedef {import('@/store/modules/bulkSend/models/Templates').TTemplates} TTemplates */

export default defineComponent({
  components: { RolesSelector, BaseButton },
  setup() {
    /**
     * @param {TUpdateParties} parties
     * @param {TTemplates} templates
     */
    function createExample(parties, templates) {
      const partiesRef = ref(parties);
      const templatesRef = ref(templates);

      return {
        parties,
        templates,
        ...useBulkSendRoles(partiesRef, templatesRef),
      };
    }

    const roles = createTemplateParties(12);
    const examples = [
      createExample(
        createNewParties(2),
        createTemplates(1, [{ parties: [roles[0], roles[1]] }]),
      ),
      createExample(
        createNewParties(2, [{ document_party_ids: [roles[0].id] }]),
        createTemplates(1, [{ parties: [roles[0], roles[1]] }]),
      ),
      createExample(
        createNewParties(1),
        createTemplates(1, [{ parties: [roles[2]] }]),
      ),
      createExample(
        createNewParties(1),
        createTemplates(1, [{ title: 'Template without roles', parties: [] }]),
      ),
      createExample(
        createNewParties(4, [
          { document_party_ids: [roles[3].id] },
          { document_party_ids: [roles[4].id] },
          {},
          { role: 'copy' },
        ]),
        createTemplates(3, [
          { title: faker.lorem.sentence(), parties: [roles[3]] },
          { parties: [roles[4]] },
          { parties: [roles[5]] },
        ]),
      ),
      createExample(
        createNewParties(2, [
          { document_party_ids: [roles[6].id] },
          { document_party_ids: [roles[7].id] },
        ]),
        createTemplates(2, [
          { parties: [roles[6], roles[7]] },
          { parties: [roles[8]] },
        ]),
      ),
      createExample(
        createNewParties(3, [
          { document_party_ids: [roles[9].id] },
          { document_party_ids: [roles[10].id, roles[11].id] },
        ]),
        createTemplates(2, [
          { parties: [roles[9], roles[10]] },
          { parties: [roles[11]] },
        ]),
      ),
    ];

    const showParties = ref(false);

    return {
      showParties,
      examples,
    };
  },
});
</script>
